import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { HiChevronDoubleLeft } from "react-icons/hi2";

import { SidebarMenuCard, SidebarMenuItem } from "../../types/types"
import { classNames } from "../../utils/classes";
import { ROUTES } from "../../helpers/constants";

import { SidebarMenuItemComponent } from "./sidebar-menu-item";
import { NavbarMenu } from "../navbar/navbar-menu";
import NewVersionBanner from "../widgets/new-version-banner";

import GrowmeLogo from '../../assets/growme-logo-light.svg';
import GrowmeName from '../../assets/growme-name-logo-light.svg';

interface props {
  items: SidebarMenuItem[];
  // Ítems pegados al fondo del menú (p. ej. la vista previa del test).
  bottomItems?: SidebarMenuItem[];
  card: SidebarMenuCard;
}

const COLLAPSED_KEY = 'gm-sidebar-collapsed';

const readCollapsed = () => {
  try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; }
};

// Estructura del panel: menú lateral siempre visible (contraíble a solo íconos
// con la flecha del borde), barra superior y el contenido de la página. En
// pantallas chicas el menú se abre como panel deslizable desde la barra superior.
export const SidebarMenu: React.FC<props> = ({ items, bottomItems = [], card }) => {
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Al navegar se cierra el menú deslizable del celular.
  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0'); } catch { /* sin almacenamiento */ }
      return next;
    });
  };

  const isActive = (item: SidebarMenuItem) => {
    const routes = [item.url, ...(item.match || [])];
    return routes.some((route) => pathname === route || pathname.startsWith(`${route}/`));
  };

  return (
    <div className={classNames('gm-app', collapsed ? 'gm-app--collapsed' : '', drawerOpen ? 'gm-app--drawer' : '')}>
      <aside id='sidebar' className="gm-sidebar" aria-label="Menú principal">
        <button
          type="button"
          className="gm-sidebar-brand"
          onClick={() => navigate(ROUTES.HOME.route)}
          title="Ir al inicio"
        >
          <img className="gm-sidebar-mark" src={GrowmeLogo} alt="" />
          <img className="gm-sidebar-word gm-hide-collapsed" src={GrowmeName} alt="GrowMe" />
        </button>
        <nav className="gm-sidebar-nav">
          {items.map((item) => (
            <SidebarMenuItemComponent key={item.id} item={item} active={isActive(item)} />
          ))}
        </nav>
        {bottomItems.length > 0 && (
          <nav className="gm-sidebar-nav gm-sidebar-nav--bottom" aria-label="Herramientas">
            {bottomItems.map((item) => (
              <SidebarMenuItemComponent key={item.id} item={item} active={isActive(item)} />
            ))}
          </nav>
        )}
        <button
          type="button"
          className="gm-sidebar-edge"
          onClick={toggleCollapsed}
          title={collapsed ? 'Expandir menú' : 'Contraer menú'}
          aria-label={collapsed ? 'Expandir menú' : 'Contraer menú'}
        >
          <HiChevronDoubleLeft size={16} />
        </button>
      </aside>
      <div className="gm-sidebar-scrim" onClick={() => setDrawerOpen(false)} />
      <div className="gm-main">
        <NewVersionBanner />
        <NavbarMenu toggle={() => setDrawerOpen(true)} card={card} />
        <div className="gm-page-container">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
